import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';

import { NavItem } from '../../models/navItem';
import { ConfigService } from '../config/config.service';
import { NavigationRouteService } from '../navigation-route/navigation-route.service';
import { LoggingService } from '../shared/logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authToken = signal<string | null>(this.getStoredToken());
  private rememberMeEnabled = signal<boolean>(localStorage.getItem('rememberMe') === 'true');

  userName = signal<string>(this.extractUserName(this.authToken()));
  userRole = signal<string>(this.extractUserRole(this.authToken()));
  isAuthenticated = signal<boolean>(!!this.authToken());

  // Signal for authentication state changes - modern Angular approach
  private _authenticationStateChange = signal<boolean>(!!this.authToken());
  public readonly authenticationStateChange = this._authenticationStateChange.asReadonly();

  private readonly keycloak = inject(Keycloak);
  private beforeUnloadListener?: () => void;
  private readonly SESSION_TIMESTAMP_KEY = 'sessionTimestamp';
  private readonly SESSION_ACTIVE_KEY = 'sessionActive';
  private sessionCheckInterval?: number;

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private navigationRouteService: NavigationRouteService,
    private loggingService: LoggingService
  ) {
    // Initialize Keycloak
    this.updateUserInfoFromKeycloak();
    
    // Set up session management
    this.setupSessionManagement();
  }

  login(): Promise<void> {
    // Redirect to Keycloak login
    return this.keycloak.login();
  }

  logout(skipKeycloakLogout: boolean = false) {
    this.removeBeforeUnloadListener();
    
    if (skipKeycloakLogout) {
      this.resetAuthState();
      this._authenticationStateChange.set(false);
      return;
    }
    
    this.keycloak.logout({ redirectUri: window.location.origin + '/' }).then(() => {
      this.resetAuthState();
      this._authenticationStateChange.set(false);
    }).catch((error: any) => {
      this.loggingService.error('AuthService', 'Logout failed:', error);
      // Still reset local state even if Keycloak logout fails
      this.resetAuthState();
      this._authenticationStateChange.set(false);
    });
  }

  private extractUserName(token: string | null): string {
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Try multiple fields for user name from Keycloak token
      const name = payload.given_name || payload.name || payload.preferred_username || payload.sub || '';
      return this.capitalizeFirstCharacter(name);
    } catch {
      return '';
    }
  }

  private extractUserRole(token: string | null): string {
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Check for roles in the Keycloak token format
      const roles = payload.roles || payload.realm_access?.roles || [];
      if (roles.includes('administrator')) {
        return 'Administrator';
      } else if (roles.includes('tournament-manager')) {
        return 'Tournament Manager';
      } else if (roles.includes('user')) {
        return 'User';
      }
      // Fallback to the existing Microsoft claims format
      const msRole = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '';
      if (msRole) {
        return this.capitalizeFirstCharacter(msRole);
      }
      return roles.length > 0 ? this.capitalizeFirstCharacter(roles[0]) : '';
    } catch {
      return '';
    }
  }

  private capitalizeFirstCharacter(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private async updateUserInfoFromKeycloak(): Promise<void> {
    if (this.keycloak.authenticated) {
      const token = this.keycloak.token;

      this.loggingService.debug('AuthService', 'Updating user info from Keycloak', { hasToken: !!token });

      // Update signals with Keycloak user info
      this.authToken.set(token || null);

      // Get user info from token claims instead of calling loadUserProfile
      if (token) {
        const userName = this.extractUserName(token);
        const userRole = this.extractUserRole(token);

        this.userName.set(userName);
        this.userRole.set(userRole);
        this.isAuthenticated.set(true);
        this._authenticationStateChange.set(true);

        // Store token based on Remember Me setting
        this.storeToken(token);
        this.loggingService.debug('AuthService', 'Token stored', { rememberMe: this.rememberMeEnabled() });
        this.loggingService.debug('AuthService', 'User info extracted from token', { userName, userRole });
      }
    } else {
      this.loggingService.debug('AuthService', 'User not authenticated, resetting auth state');
      this.resetAuthState();
    }
  }

  private resetAuthState(): void {
    // Clean up session detection
    this.removeBeforeUnloadListener();
    this.clearSessionCheckInterval();
    
    // Clear all stored tokens and session data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem(this.SESSION_TIMESTAMP_KEY);
    localStorage.removeItem('browserClosing');
    sessionStorage.removeItem(this.SESSION_ACTIVE_KEY);
    
    // Reset signals
    this.authToken.set(null);
    this.userName.set('');
    this.userRole.set('');
    this.isAuthenticated.set(false);
    this.rememberMeEnabled.set(false);
    this._authenticationStateChange.set(false);
  }

  // private extractRoleFromKeycloak(roles: string[]): string {
  //   // Map Keycloak roles to display names
  //   if (roles.includes('administrator')) {
  //     return 'Administrator';
  //   } else if (roles.includes('tournament-manager')) {
  //     return 'Tournament Manager';
  //   } else if (roles.includes('user')) {
  //     return 'User';
  //   }
  //   return roles.length > 0 ? this.capitalizeFirstCharacter(roles[0]) : '';
  // }

  async getValidToken(): Promise<string | null> {
    this.loggingService.debug('AuthService', 'Getting token from Keycloak...');

    // Update token if needed
    try {
      await this.keycloak.updateToken(30); // Refresh if expires within 30 seconds
    } catch (error) {
      this.loggingService.warn('AuthService', 'Token refresh failed', error);
      return null;
    }

    const token = this.keycloak.token;
    this.loggingService.debug('AuthService', 'Token from Keycloak', { hasToken: !!token });
    if (token) {
      // Store token based on Remember Me setting
      this.storeToken(token);
      this.authToken.set(token);
      this.loggingService.debug('AuthService', 'Token refreshed and stored');
    } else {
      this.loggingService.debug('AuthService', 'No token to store');
    }
    return token || null;
  }

  getNavItems(): NavItem[] | undefined {
    // Convert navigation routes to nav items and filter based on visibility
    const navigationRoutes = this.configService.config?.navigation || [];
    const navItems = this.navigationRouteService.convertToNavItems(navigationRoutes);

    // Return all nav items since hidden property has been removed
    return navItems;
  }

  /**
   * Get the current access token
   * @returns The current access token or null if not authenticated
   */
  getAccessToken(): string | null {
    return this.authToken();
  }

  setRememberMe(enabled: boolean): void {
    this.rememberMeEnabled.set(enabled);
    localStorage.setItem('rememberMe', enabled.toString());
    
    // If we have a current token, re-store it with the new setting
    const currentToken = this.authToken();
    if (currentToken) {
      this.storeToken(currentToken);
    }
    
    // Update session management
    this.setupSessionManagement();
    
    this.loggingService.debug('AuthService', 'Remember Me setting updated', { enabled });
  }

  getRememberMe(): boolean {
    return this.rememberMeEnabled();
  }

  private getStoredToken(): string | null {
    // Check localStorage first (persistent), then sessionStorage (session-only)
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  }

  private storeToken(token: string): void {
    // Always store in localStorage - let Keycloak handle session persistence
    // The Keycloak session cookies will determine actual session behavior
    localStorage.setItem('authToken', token);
    sessionStorage.removeItem('authToken');
  }

  private setupSessionManagement(): void {
    this.removeBeforeUnloadListener();
    this.clearSessionCheckInterval();
    
    if (this.isAuthenticated()) {
      if (!this.rememberMeEnabled()) {
        // Set up browser close detection when Remember Me is disabled
        this.setupBrowserCloseDetection();
        this.loggingService.debug('AuthService', 'Browser close detection enabled');
      } else {
        this.loggingService.debug('AuthService', 'Remember Me enabled - persistent session');
      }
      
      // Update session timestamp
      this.updateSessionTimestamp();
    }
  }

  private removeBeforeUnloadListener(): void {
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = undefined;
    }
  }

  private clearSessionCheckInterval(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = undefined;
    }
  }

  private setupBrowserCloseDetection(): void {
    // Set session marker for browser close detection
    sessionStorage.setItem(this.SESSION_ACTIVE_KEY, 'true');
    this.updateSessionTimestamp();
    
    // Clean beforeunload handler
    this.beforeUnloadListener = () => {
      if (!this.rememberMeEnabled()) {
        localStorage.setItem('forceLogout', 'true');
      }
    };
    
    window.addEventListener('beforeunload', this.beforeUnloadListener);
  }


  private validateSession(): void {
    if (!this.isAuthenticated() || this.rememberMeEnabled()) {
      return;
    }
    
    // Check if session storage still exists (browser not closed)
    const sessionActive = sessionStorage.getItem(this.SESSION_ACTIVE_KEY);
    
    if (!sessionActive) {
      this.loggingService.debug('AuthService', 'Session storage cleared - browser may have been closed');
      this.logout(true);
    } else {
      // Update timestamp to show activity
      this.updateSessionTimestamp();
    }
  }

  private updateSessionTimestamp(): void {
    const timestamp = Date.now().toString();
    localStorage.setItem(this.SESSION_TIMESTAMP_KEY, timestamp);
    if (!this.rememberMeEnabled()) {
      sessionStorage.setItem(this.SESSION_ACTIVE_KEY, 'true');
    }
  }

  private clearAuthCookies(): void {
    // Try to clear common auth cookies by setting them to expire
    const cookiesToClear = [
      'KEYCLOAK_SESSION',
      'KEYCLOAK_IDENTITY', 
      'KEYCLOAK_IDENTITY_LEGACY',
      'AUTH_SESSION_ID',
      'KC_RESTART'
    ];
    
    cookiesToClear.forEach(cookieName => {
      // Try different domain combinations
      const domains = ['', '.localhost', window.location.hostname];
      domains.forEach(domain => {
        const domainStr = domain ? `; domain=${domain}` : '';
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainStr}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/auth${domainStr}`;
      });
    });
  }


}
